/** A translation catalog: nested objects whose leaves are ICU messages. */
export interface Catalog {
    [key: string]: string | Catalog;
}

/**
 * Merges the catalogs of the platform and of each product into one resource. Each catalog owns a single top-level
 * key (`platform`, `tenders`…), so two owners can never overwrite each other's texts.
 */
export function mergeCatalogs(catalogs: Record<string, Catalog>): Catalog {
    const merged: Catalog = {};
    for (const [owner, catalog] of Object.entries(catalogs)) {
        const keys = Object.keys(catalog);
        if (keys.length !== 1 || keys[0] !== owner) {
            throw new Error(
                `Catalog of "${owner}" must contain the single top-level key "${owner}", got: ${keys.join(', ')}`,
            );
        }
        merged[owner] = catalog[owner] as Catalog;
    }
    return merged;
}

/** Flattens a catalog to dotted keys: `{ tenders: { sheet: { title } } }` → `tenders.sheet.title`. */
export function flattenCatalog(catalog: Catalog, prefix = ''): Map<string, string> {
    const entries = new Map<string, string>();
    for (const [key, value] of Object.entries(catalog)) {
        const path = prefix ? `${prefix}.${key}` : key;
        if (typeof value === 'string') {
            entries.set(path, value);
        } else {
            for (const [child, message] of flattenCatalog(value, path)) {
                entries.set(child, message);
            }
        }
    }
    return entries;
}

export function mapCatalog(catalog: Catalog, transform: (message: string) => string): Catalog {
    return Object.fromEntries(
        Object.entries(catalog).map(([key, value]) => [
            key,
            typeof value === 'string' ? transform(value) : mapCatalog(value, transform),
        ]),
    );
}
